import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, OutlinedInput, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { sxFormError, sxLabel } from "../../../../assets/styles/CommonSxProps";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { Contrato } from "../../../../interfaces/entities/Cancelamento/PassoSelecioneBeneficiarios/Contrato";
import { SendRetencaoValidation } from "../../../../interfaces/validation/Cancelamento/SendRetencaoValidation";
import { enviarRetencaoPf } from "../../../../services/ContratoService";
import { cellphoneMask } from "../../../../utils/PhoneRegex";
import { sxTitle } from "../../PermissoesVisualizacao/sxProps";
import { sxButton } from "../SolicitacaoCancelamento/sxProps";
import "./styles.css";
import {
  sxBoxSendRetentionForm,
  sxInputSendRetentionFormFull,
} from "./sxProps";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import LoadingApp from "../../../../components/LoadingApp";

const EnviarRetencao: React.FC<{
  motivoCancelamento: string;
  benefs: Contrato[];
  tipoCancelamento: string;
}> = ({ motivoCancelamento, benefs, tipoCancelamento }) => {
  const navigate = useNavigate();
  const { fontSizeStyleAccessibility, fontSizeTitleStyleAccessibility } =
    useAccessibility();
  const [loading, setLoading] = useState<boolean>(false)

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: "error" | "success" | "warning";
    title?: string;
    textButton?: string;
    subTitle?: string;
    closeAction: () => void;
    showButtonModal: boolean;
    buttonAction?: () => void;
  }>(null);

  const formik = useFormik({
    initialValues: {
      email: "",
      tel: "",
    },
    validationSchema: SendRetencaoValidation,
    onSubmit: (values) => {
      sendRetencaoPf(values.email, values.tel, motivoCancelamento, benefs);
    },
    validateOnChange: false,
  });

  const sendRetencaoPf = async (
    email: string,
    telefone: string,
    motivoCancelamento: string,
    benefs: Contrato[]
  ) => {
    setLoading(true)
    const retencaoResponse = await enviarRetencaoPf(
      telefone,
      email,
      motivoCancelamento,
      benefs,
      tipoCancelamento
    );
    if (retencaoResponse?.status === 200) {
      openModalSuccess(retencaoResponse.data);
    } else {
      const errorMessage = !!retencaoResponse?.data.mensagem
        ? retencaoResponse.data.mensagem
        : "Tente novamente em instantes";
      openModalError("Erro", errorMessage);
      console.error(retencaoResponse);
    }
    setLoading(false)
  };

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: "error",
      title: title,
      subTitle: subtitle,
      textButton: "Fechar",
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null),
    });
  };

  const openModalSuccess = (mensagem: string) => {
    setModalFeedback({
      icon: "success",
      title: "Solicitação recebida!",
      subTitle: mensagem ?? "Sua solicitação foi recebida pela equipe comercial. Entraremos em contato em dias úteis, durante o horário comercial, para dar prosseguimento à finalização do processo, utilizando os canais de telefone e e-mail informados.<br>O atendimento do plano está suspenso até a conclusão do processo.",
      textButton: "Voltar para o início",
      closeAction: () => navigate("/home"),
      buttonAction: () => navigate("/home"),
      showButtonModal: true,
    });
  };

  return (
    <>
      <div className="cancellation-container">
        <Typography style={fontSizeTitleStyleAccessibility} sx={sxTitle}>
          Envie um e-mail para a equipe de pós-vendas
        </Typography>
        <form onSubmit={formik.handleSubmit} className="form-change-data">
          <Box sx={sxBoxSendRetentionForm}>
            <div className="send-retention-form-labeled-outlined-input">
              <Typography style={fontSizeStyleAccessibility} sx={sxLabel}>
                E-mail
              </Typography>
              <OutlinedInput
                type={"text"}
                value={formik.values.email}
                onChange={(e) => {
                  formik.setFieldValue("email", e.target.value);
                }}
                name="email"
                placeholder="email@email.com"
                error={!!formik.errors.email}
                maxRows={1}
                inputProps={{ maxLength: 100 }}
                sx={sxInputSendRetentionFormFull}
              />
              {formik.errors.email && (
                <Typography
                  style={fontSizeStyleAccessibility}
                  tabIndex={0}
                  sx={sxFormError}
                >
                  {formik.errors.email}
                </Typography>
              )}
            </div>
            <div className="send-email-form-labeled-outlined-input">
              <Typography style={fontSizeStyleAccessibility} sx={sxLabel}>
                Telefone
              </Typography>
              <OutlinedInput
                type={"text"}
                value={formik.values.tel}
                onChange={(e) => {
                  formik.setFieldValue("tel", cellphoneMask(e.target.value));
                }}
                name="tel"
                placeholder="(99) 99999-9999"
                error={!!formik.errors.tel}
                maxRows={1}
                inputProps={{ maxLength: 15 }}
                sx={sxInputSendRetentionFormFull}
              />
              {formik.errors.tel && (
                <Typography
                  style={fontSizeStyleAccessibility}
                  tabIndex={0}
                  sx={sxFormError}
                >
                  {formik.errors.tel}
                </Typography>
              )}
            </div>
            <Button
              variant="contained"
              size="large"
              type="submit"
              sx={sxButton}
              disabled={!formik.values.email || !formik.values.tel}
            >
              <Typography style={fontSizeStyleAccessibility}>Enviar</Typography>
            </Button>
            <button className="back-button" style={fontSizeStyleAccessibility}>
              <FontAwesomeIcon icon={faChevronLeft} />
              &nbsp;&nbsp;<u>Voltar</u>
            </button>
          </Box>
        </form>
      </div>
      <LoadingApp showLoading={loading} />
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as "success" | "error" | "warning"}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
        classNameModal={`modal-custom ${modalFeedback?.icon === 'error' ? "error" : ''}`}
      />
    </>
  );
};

export default EnviarRetencao;
